import React, { useEffect, useState } from 'react';
import { Button, Chip, Notice, Select, Sheet, TextInput } from '@origo/origo-ui';
import { SlidersHorizontalIcon, WarningIcon } from '@phosphor-icons/react';
import {
    type AgolLayerInfo,
    FIELD_DATA_TYPE_LABEL,
    LOKAL_ID_SOURCE_PATH,
    MATCH_KIND_LABEL,
    type UnmappedValuePolicy,
    type ValueMapping,
} from '../../../api/types';
import {
    type FieldDraft,
    type LayerDraft,
    findAgolField,
    isLayerAssigned,
    mappableAgolLayers,
    sameGeometry,
    withAgolField,
} from './draft';
import { ValueMapEditor } from './ValueMapEditor';
import styles from './create.module.css';

interface LayerMappingPanelProps {
    layer: LayerDraft;
    availableAgolLayers: AgolLayerInfo[];
    onLayerChange: (target: AgolLayerInfo | null) => void;
    onFieldChange: (ngisSourcePath: string, patch: Partial<FieldDraft>) => void;
    /** AGOL-lag-id → variant som allerede bruker laget (andre objekttyper enn denne). */
    usedBy?: ReadonlyMap<number, string>;
}

/** Kort geometrinavn fra ESRI-type ("esriGeometryPolygon" → "Polygon"). */
function shortGeometry(esri: string): string {
    return esri.replace(/^esriGeometry/, '') || esri;
}

/** Høyre panel: overstyr objekttype ↔ lag, og mapp felt + domener for valgt lag. */
export const LayerMappingPanel: React.FC<LayerMappingPanelProps> = ({
    layer,
    availableAgolLayers,
    onLayerChange,
    onFieldChange,
    usedBy,
}) => {
    // NGIS-stien til feltet hvis domene-/verdimap åpnes i side-panelet.
    const [domainField, setDomainField] = useState<string | null>(null);

    // Lukk domene-panelet når brukeren bytter objekttype (eller geometrivariant av samme).
    useEffect(() => {
        setDomainField(null);
    }, [layer.featureType, layer.geometryKind, layer.agolLayerId]);

    const assigned = isLayerAssigned(layer);
    // Hver geometrivariant har sitt eget AGOL-sublag: vis bare lag med samme geometri. Et allerede
    // koblet lag beholdes i listen selv om geometrien er feil, så feilen synes og kan rettes.
    const selectableLayers = mappableAgolLayers(availableAgolLayers).filter(
        (l) => sameGeometry(layer.geometryKind, l.geometryType) || l.id === layer.agolLayerId,
    );
    const canPickLayer = selectableLayers.length > 0;
    const hasFieldDropdown = layer.availableAgolFields.length > 0;
    const openField = domainField
        ? layer.fields.find((f) => f.ngisSourcePath === domainField) ?? null
        : null;

    // Domene kan settes opp når preflight foreslo en verdimap, eller når NGIS-feltet har
    // kodeliste og er mappet mot et AGOL-felt (f.eks. etter manuelt lagvalg).
    const canEditDomain = (field: FieldDraft) =>
        field.valueMap !== null ||
        ((field.ngisCodedValues?.length ?? 0) > 0 && field.agolFieldName.trim().length > 0);

    const layerOptions = [
        { value: '', label: '— ikke koblet —' },
        ...selectableLayers.map((l) => {
            const owner = l.id === layer.agolLayerId ? undefined : usedBy?.get(l.id);
            const wrongGeometry = !sameGeometry(layer.geometryKind, l.geometryType);
            const note = wrongGeometry ? ' — feil geometri' : owner ? ` — brukt av ${owner}` : '';
            return {
                value: String(l.id),
                label: `${l.name} (id ${l.id}) · ${shortGeometry(l.geometryType)}${note}`,
                disabled: owner !== undefined,
            };
        }),
    ];

    const fieldOptions = hasFieldDropdown
        ? [
              { value: '', label: '— ikke mappet —' },
              ...layer.availableAgolFields.map((f) => ({
                  value: f.name,
                  label: f.alias && f.alias !== f.name ? `${f.name} (${f.alias})` : f.name,
              })),
          ]
        : [];

    const handleLayerSelect = (value: string) => {
        if (!value) {
            onLayerChange(null);
            return;
        }
        const target = availableAgolLayers.find((l) => l.id === Number(value)) ?? null;
        onLayerChange(target);
    };

    return (
        <div className={styles.editor}>
            <div>
                <h2 className={styles.editorTitle}>
                    {layer.featureType}
                    <Chip variant="info">{layer.geometryKind}</Chip>
                </h2>
                <p className={styles.muted}>
                    Velg hvilket AGOL-lag denne objekttypen kobles til, og mapp NGIS-feltene mot
                    lagets felter. Feltet <code>identifikasjon.lokalId</code> er påkrevd — det kobler
                    hvert objekt mot NGIS.
                </p>
            </div>

            <div className={styles.fieldGroup}>
                <p className={styles.sectionHeading}>AGOL-lag</p>
                {canPickLayer ? (
                    <Select
                        label="AGOL-lag"
                        hideLabel
                        value={assigned ? String(layer.agolLayerId) : ''}
                        options={layerOptions}
                        helperText="Overstyr koblingen hvis preflight matchet feil lag."
                        onChange={(e) => handleLayerSelect(e.target.value)}
                    />
                ) : assigned ? (
                    <div className={styles.summaryGrid}>
                        <span className={styles.summaryLabel}>AGOL-lag</span>
                        <span>
                            {layer.agolLayerName}{' '}
                            <span className={styles.muted}>(id {layer.agolLayerId})</span>
                        </span>
                    </div>
                ) : (
                    <Notice variant="warning">
                        Dette laget ble ikke matchet mot et AGOL-lag, og tjenesten leverer ikke
                        lag-listen som trengs for å overstyre. Sjekk tjeneste-URL-en.
                    </Notice>
                )}

                {assigned && (
                    <div className={styles.summaryGrid}>
                        <span className={styles.summaryLabel}>OBJECTID-felt</span>
                        <span className={styles.mono}>{layer.agolObjectIdField}</span>
                        <span className={styles.summaryLabel}>GlobalID-felt</span>
                        <span className={styles.mono}>{layer.agolGlobalIdField}</span>
                    </div>
                )}
            </div>

            {!assigned ? (
                <Notice variant="info">
                    Objekttypen er ikke koblet til noe AGOL-lag og utelates fra koblingen. Velg et lag
                    over for å mappe felt.
                </Notice>
            ) : (
                <div>
                    <p className={styles.sectionHeading}>Feltmapping</p>
                    {layer.fields.length === 0 && (
                        <p className={styles.muted}>Ingen NGIS-felt funnet for dette laget.</p>
                    )}
                    {layer.fields.map((field) => {
                        const isLokalId = field.ngisSourcePath === LOKAL_ID_SOURCE_PATH;
                        const missingLokalId = isLokalId && field.agolFieldName.trim().length === 0;
                        return (
                            <div
                                key={field.ngisSourcePath}
                                className={`${styles.fieldRow} ${isLokalId ? styles.fieldRowRequired : ''}`}
                            >
                                <div>
                                    <div className={styles.ngisPath}>
                                        {field.ngisSourcePath}
                                        {isLokalId && <Chip variant="danger">påkrevd</Chip>}
                                    </div>
                                    <div className={styles.fieldMeta}>
                                        <Chip variant="default">
                                            {FIELD_DATA_TYPE_LABEL[field.dataType]}
                                        </Chip>
                                        <Chip variant={field.matchKind === 'None' ? 'warning' : 'success'}>
                                            {MATCH_KIND_LABEL[field.matchKind] ?? field.matchKind}
                                        </Chip>
                                        {!field.typeCompatible && field.agolFieldName && (
                                            <Chip variant="warning">
                                                <WarningIcon size={12} weight="bold" /> type-avvik
                                            </Chip>
                                        )}
                                    </div>
                                </div>

                                {hasFieldDropdown ? (
                                    <Select
                                        label="AGOL-felt"
                                        hideLabel
                                        value={field.agolFieldName}
                                        error={missingLokalId ? 'Må mappes' : undefined}
                                        options={fieldOptions}
                                        onChange={(e) =>
                                            onFieldChange(
                                                field.ngisSourcePath,
                                                withAgolField(
                                                    field,
                                                    e.target.value,
                                                    layer.availableAgolFields,
                                                ),
                                            )
                                        }
                                    />
                                ) : (
                                    <TextInput
                                        label="AGOL-felt"
                                        hideLabel
                                        placeholder="AGOL-feltnavn"
                                        value={field.agolFieldName}
                                        error={missingLokalId ? 'Må mappes' : undefined}
                                        onChange={(e) =>
                                            onFieldChange(field.ngisSourcePath, {
                                                agolFieldName: e.target.value,
                                            })
                                        }
                                    />
                                )}

                                {canEditDomain(field) && (
                                    <div className={styles.domainTrigger}>
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            leftIcon={<SlidersHorizontalIcon size={14} weight="bold" />}
                                            onClick={() => setDomainField(field.ngisSourcePath)}
                                        >
                                            {field.valueMap && field.valueMap.length > 0
                                                ? `Domene · ${field.valueMap.length} kode${field.valueMap.length === 1 ? '' : 'r'}`
                                                : 'Legg til domene'}
                                        </Button>
                                        {field.unmatchedCodes && field.unmatchedCodes.length > 0 && (
                                            <Chip variant="warning">
                                                {field.unmatchedCodes.length} uten treff
                                            </Chip>
                                        )}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}

            {openField && canEditDomain(openField) && (
                <Sheet
                    open
                    onClose={() => setDomainField(null)}
                    title={`Domene · ${openField.ngisSourcePath}`}
                >
                    <ValueMapEditor
                        valueMap={openField.valueMap ?? []}
                        unmatchedCodes={openField.unmatchedCodes}
                        ngisCodes={openField.ngisCodedValues}
                        agolCodes={
                            findAgolField(layer.availableAgolFields, openField.agolFieldName)
                                ?.codedValues ?? null
                        }
                        onUnmapped={openField.onUnmapped}
                        onChange={(valueMap: ValueMapping[]) =>
                            onFieldChange(openField.ngisSourcePath, { valueMap })
                        }
                        onPolicyChange={(onUnmapped: UnmappedValuePolicy) =>
                            onFieldChange(openField.ngisSourcePath, { onUnmapped })
                        }
                    />
                </Sheet>
            )}
        </div>
    );
};
